import { ExpenseTagColorEnum, ExpenseTag as ExpenseTagModel } from "@finway-group/shared/lib/models"
import React from "react"
import { useTranslation } from "react-i18next"
import { useExpenseTagsByIds, useExpenseTagsInLoadingState } from "~Shared/hooks/expenseTags.hooks"
import { ExpenseTagService, NotificationService } from "~Shared/services"
import DialogService from "~Shared/services/dialog.service"
import ExpenseTag from "./expenseTag"
import ExpenseTagManagerPopover from "./popovers/expenseTagManager.popover"
import ExpenseTagViewer from "./popovers/expenseTagViewer.popover"

interface TagSectionProps {
    entityTags: Array<string>
    hideButton?: boolean
    hideTags?: boolean
    maxTags?: number
    tagClassName?: string
    onTagCreate: (tagColor: ExpenseTagColorEnum, tagName: string) => Promise<void>
    onTagDelete: (expenseTagId: string, tagName: string) => Promise<void>
    onTagSelected: (expenseTagId: string) => Promise<void>
    onTagDetach: (expenseTagId: string) => Promise<void>
}

export const TagSection = ({
    entityTags,
    hideButton = false,
    hideTags = false,
    maxTags = 3,
    tagClassName = "",
    onTagCreate,
    onTagDelete,
    onTagSelected,
    onTagDetach,
}: TagSectionProps) => {
    const { t } = useTranslation()

    const attachedTags = useExpenseTagsByIds(entityTags)
    const { expenseTagsLoading, addTagInLoading, removeTagFromLoading } = useExpenseTagsInLoadingState()

    const handleTagDetach = async (expenseTagId: string) => {
        try {
            addTagInLoading(expenseTagId)
            await onTagDetach(expenseTagId)
        } catch (err) {
            NotificationService.showErrorNotificationBasedOnResponseError(err, t("error:expense_tag.detach.title"))
        } finally {
            removeTagFromLoading(expenseTagId)
        }
    }

    const handleTagSelected = async (expenseTagId: string) => {
        try {
            addTagInLoading(expenseTagId)
            await onTagSelected(expenseTagId)
        } catch (err) {
            NotificationService.showErrorNotificationBasedOnResponseError(err, t("error:expense_tag.attach.title"))
        } finally {
            removeTagFromLoading(expenseTagId)
        }
    }

    const handleTagCreate = async (tagColor: ExpenseTagColorEnum, tagName: string) => {
        try {
            await onTagCreate(tagColor, tagName)
        } catch (err) {
            NotificationService.showErrorNotificationBasedOnResponseError(err, t("error:expense_tag.create.title"))
        }
    }

    const handleTagDelete = async (expenseTagId: string, tagName: string) => {
        try {
            const data = await ExpenseTagService.getTotalTagAttachCount(expenseTagId)
            if (await DialogService.confirmTagDelete(tagName, data.totalCount)) {
                try {
                    await onTagDelete(expenseTagId, tagName)
                } catch (err) {
                    NotificationService.showErrorNotificationBasedOnResponseError(err, t("error:expense_tag.delete.title"))
                }
            }
        } catch (err) {
            NotificationService.showErrorNotificationBasedOnResponseError(err, t("error:expense_tag.delete.title"))
        }
    }

    const renderExpenseTags = (expenseTags: Array<ExpenseTagModel>) =>
        expenseTags.map((expenseTag) => (
            <ExpenseTag
                className={tagClassName}
                key={expenseTag._id}
                color={expenseTag.tagColor}
                onClose={() => handleTagDetach(expenseTag._id)}
                closable={!expenseTagsLoading.includes(expenseTag._id)}
                isLoading={expenseTagsLoading.includes(expenseTag._id)}
            >
                <div className="max-w-240 truncate">{expenseTag.tagName}</div>
            </ExpenseTag>
        ))

    const renderExpenseTagsFormatted =
        attachedTags.length > maxTags ? (
            <>
                {renderExpenseTags(attachedTags.slice(0, maxTags - 1))}
                <ExpenseTagViewer expenseTags={attachedTags.slice(maxTags - 1)} onDelete={handleTagDetach}>
                    <ExpenseTag className={tagClassName} color={attachedTags[maxTags - 1].tagColor}>
                        + {attachedTags.slice(maxTags - 1).length}
                    </ExpenseTag>
                </ExpenseTagViewer>
            </>
        ) : (
            renderExpenseTags(attachedTags)
        )

    return (
        <>
            {!hideTags && renderExpenseTagsFormatted}
            {!hideButton && <ExpenseTagManagerPopover existingTags={attachedTags} onTagSelected={handleTagSelected} onTagCreate={handleTagCreate} onTagDelete={handleTagDelete} />}
        </>
    )
}
